// ─────────────────────────────────────────────────────────────
// Shared types for the Maps Discover Studio SPA.
// ─────────────────────────────────────────────────────────────

import type {
  AcquisitionMetadata,
  DerivedTags,
  MarieChatMetaFrame,
  MetadataSource,
  UpstreamError,
} from "../../api/client";

// Re-export DerivedTags so it can be imported from the same module other
// scriptHelper code already imports its image-shape types from.
export type { DerivedTags } from "../../api/client";

// Re-export ScriptVersion so PythonTab + modals can pull it from the same
// types module they use for the rest of the editor's domain types.
export type { ScriptVersion } from "../../api/client";

export type ScriptHelperTab =
  | "welcome"
  | "python"
  | "output"
  | "imageLibrary"
  | "scripts"
  | "community"
  | "help"
  | "settings";

/** Controls Code/Chat split on the Editor (python) tab. */
export type EditorMode = "code" | "chat";

export interface ChatMessage {
  id: string;
  /**
   * "user" / "assistant" are normal chat turns rendered as bubbles.
   * "checkpoint" is a system-event marker (e.g. "you restored Marie's earlier
   * version") rendered inline as a centered hairline-bordered row instead of
   * a chat bubble. Checkpoints carry no extractedCode and are skipped by the
   * active-version auto-follow effect — they're metadata, not content.
   */
  role: "user" | "assistant" | "checkpoint";
  content: string;
  /** When present, a syntax-highlighted code block is rendered below the text content. */
  streamingCode?: string;
  /** True while the AI is still appending characters to this message's code block. */
  isStreaming?: boolean;
  /**
   * When true, this message represents an execution failure.
   * AiPanel renders "Fix It" and "View Error Details" action chips below it.
   */
  isError?: boolean;
  /**
   * Extracted Python code from a ```python ... ``` block in the assistant's content.
   * Set after streaming completes. Code is automatically injected into the Monaco editor;
   * this field is used to render the syntax-highlighted code block in the chat bubble.
   */
  extractedCode?: string;
  /**
   * Backend meta frame describing model selection + vision attachment for
   * THIS turn (assistant messages only). Populated from the leading SSE
   * meta frame. Drives the graceful-degrade warning when the backend
   * couldn't attach the image, and the per-attachment warnings shown
   * under the bubble.
   */
  meta?: MarieChatMetaFrame;
  /**
   * Whether the user had an image selected when this user/assistant turn
   * was sent. Stamped at send-time on the assistant placeholder so the
   * meta-frame UI can distinguish "no image was supposed to be attached"
   * from "image was supposed to be attached but the backend degraded".
   */
  hadImageAttached?: boolean;
  /**
   * Structured upstream-provider error payload (Sam, 2026-05-14).
   * When present, AiPanel renders an <UpstreamErrorBubble> instead of
   * a normal assistant bubble for this message — names the model + the
   * provider and gives category-specific recovery advice (wait / switch
   * / retry). Coexists with `isError` (which is used for script-execution
   * failures, a different surface).
   */
  upstreamError?: UpstreamError;
}

/**
 * Capability tier for an AI model — drives the small icon badge shown to
 * the LEFT of the model label in Marie's picker (and in the collapsed
 * trigger button). Two classes today:
 *   • "reasoning" — advanced reasoning models (Opus 4.7/4.6, GPT-5, GPT-5-mini).
 *                   Rendered with an AutoAwesomeOutlined sparkle.
 *   • "fast"      — fast / no-reasoning-overhead models (GPT-5.4).
 *                   Rendered with a BoltOutlined lightning bolt.
 *
 * The semantic difference is communicated through icon SHAPE, not colour —
 * both icons share the same `text.secondary` treatment so they read as
 * siblings, not competing. The lock icon (right side, restricted access)
 * already owns the "loud" visual lane.
 */
export type ModelCapability = "reasoning" | "fast";

export interface AiModel {
  id: string;
  label: string;
  capability: ModelCapability;
}

// Hardcoded model list. Sam (2026-05-08) wired `claude-opus-4-7` through
// the backend provider router; this entry's id must stay byte-for-byte equal
// to whatever the backend dispatches on. The user-facing `model` field on
// MarieChatRequest carries this id straight through — no client-side mapping.
//
// MUST STAY IN SYNC with `AVAILABLE_MARIE_MODELS` in `backend/app.py` —
// Sam owns the catalog; new model ids land on the backend first. The
// frontend should never introduce ids the backend doesn't dispatch on.
// (Sam, 2026-05-14: long-term plan is to drop this constant in favour of
// `available_models` from `/api/admin/role-quotas` admin-side, and
// `allowed_models` from `/api/auth/me` user-side. Until then: sync.)
//
// Order: Anthropic group first (Marie's default + expected most-used), then
// OpenAI as alternatives. The Select header on Marie's chat constrains the
// dropdown width tightly (100–130px), so we keep the list flat — adding
// MUI ListSubheader section dividers would clip awkwardly at this width.
//
// 2026-05-15 — Lineup refresh. Gemini provider deprecated (removed
// entirely). GPT-4.1 family replaced by GPT-5 family now that OpenAI
// flows through the TF GeneAI gateway. Sonnet 4.6 stays absent until
// SONNET_API_KEY is provisioned (see feedback_geneai_apim_keys).
export const AI_MODELS: AiModel[] = [
  { id: "claude-opus-4-7", label: "Claude Opus 4.7", capability: "reasoning" },
  { id: "claude-opus-4-6", label: "Claude Opus 4.6", capability: "reasoning" },
  { id: "gpt-5.4", label: "GPT-5.4", capability: "fast" },
  { id: "gpt-5-mini", label: "GPT-5-mini", capability: "reasoning" },
  { id: "gpt-5", label: "GPT-5", capability: "reasoning" },
];

export interface PlaceholderImage {
  id: string;
  name: string;
  description: string;
  tags?: string | null;
  type: "SEM" | "SDB" | "TEM" | "OPTICAL";
  isUserImage?: boolean;
  /**
   * Origin of the image record — drives `input_image_source` on Marie
   * vision-attach requests. "user" means it was clicked from the user's
   * personal `myImages` list; "library" means a shared/library image
   * (or a starter-script default). Populated by LibrarySelectModal at
   * pick time and by the starter-script preload path.
   */
  source?: "user" | "library";
  acquisition_metadata?: AcquisitionMetadata | null;
  metadata_source?: MetadataSource | null;
  derived_tags?: DerivedTags | null;
}

export interface PlaceholderScript {
  id: string;
  name: string;
  description: string;
  category?: string;
  author?: string;
  rating?: number;
  savedAt?: string;
}
